import { Button } from "@/shadcnComponents/ui/button";
import { Input } from "@/shadcnComponents/ui/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectGroup,
  SelectItem,
} from "@/shadcnComponents/ui/select";
import mixpanel from "mixpanel-browser";
import { Label } from "@/shadcnComponents/ui/label";
import {
  CampaignFormField,
  CollabFormAccordion,
  NoActivePlanAlertButton,
} from "../Components/Components";
import {
  FieldAndPlaceHolderStyles,
  handlePreview,
  postFormatListForFixed,
  postFormatListV2,
} from "../Helpers/Helper";
import { QuillEditor } from "@/Components/Editor/QuillEditor";
import { useEffect, useState } from "react";
import { RadioGroup, RadioGroupItem } from "@/shadcnComponents/ui/radio-group";
import { FancyMultiSelect } from "@/shadcnComponents/ui/multiselect";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/shadcnComponents/ui/alert-dialog";
import {
  ArrowLeft,
  ArrowRight,
  Copy,
  Eye,
  FileText,
  Plus,
  Trash2,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shadcnComponents/ui/card";
import { useNewCampaign } from "../CustomHooks/NewCampaignHooks";
import { useGeneralSettings } from "@/Providers/General";

export const ProductDetailsForm = () => {
  const {
    formData,
    setFormData,
    handleInputChange,
    handlePrevious,
    handleNext,
    handleDraftChange,
    selectedCreators,
    previewData,
  } = useNewCampaign();
  const generalState = useGeneralSettings();

  const [currentSection, setCurrentSection] = useState<"product" | "post">(
    "product"
  );
  const [allAssignedInfluencer, setAllAssignedInfluencer] = useState([]);

  const handleAddNewDraft = () => {
    mixpanel.track(
      "Brand Draft Campaign - Product Details Section: New Draft - clicked"
    );
    const newPostDraft = {
      postFormat: "",
      postDescription: "",
      postImage: [],
      postVideo: "",
      postPdf: [],
      postGuidelines: "",
      assignedInfluencers: [],
      status: 1,
    };

    setFormData((prev) => ({
      ...prev,
      postInfo: {
        ...prev.postInfo,
        postDrafts: [...prev.postInfo.postDrafts, newPostDraft],
      },
    }));
  };
  const handleDuplicateDraft = (item) => {
    mixpanel.track(
      "Brand Draft Campaign - Product Details Section: Duplicate - clicked"
    );
    const newPostDraft = {
      ...item,
      assignedInfluencers: [],
    };
    setFormData((prev) => ({
      ...prev,
      postInfo: {
        ...prev.postInfo,
        postDrafts: [...prev.postInfo.postDrafts, newPostDraft],
      },
    }));
  };
  const handleDeleteDraft = (index) => {
    mixpanel.track(
      "Brand Draft Campaign - Product Details Section: Delete - clicked"
    );
    handleDraftChange("status", 0, index);
  };
  const handleRemovePdf = (pdfIndex, index) => {
    setFormData((prev) => ({
      ...prev,
      postInfo: {
        ...prev.postInfo,
        postDrafts: prev?.postInfo?.postDrafts
          ?.filter((item) => item?.status)
          ?.map((draft, draftIndex) => {
            if (draftIndex === index) {
              return {
                ...draft,
                postPdf: draft?.postPdf?.filter((_, idx) => idx !== pdfIndex),
              };
            }
            return draft;
          }),
      },
    }));
  };

  const checkProductDetailsValidations = () => {
    let success = true;
    if (formData?.productInfo?.productName?.trim() === "") {
      generalState?.showErrorToast("Product Name is required");

      success = false;
    }

    return success;
  };
  const checkAllSelectedInfluencerValidation = () => {
    let success = true;
    selectedCreators?.forEach((influencer) => {
      if (!allAssignedInfluencer.includes(influencer)) {
        success = false;
      }
    });
    return success;
  };
  const checkAllAssignedInfluencerValidation = () => {
    let success = true;
    formData?.postInfo?.postDrafts
      ?.filter((item) => item?.status)
      ?.forEach((item) => {
        if (
          !item?.assignedInfluencers?.some((assigned) =>
            selectedCreators?.includes(assigned)
          )
        ) {
          generalState?.showErrorToast("Please remove extra drafts");

          success = false;
        }
      });

    selectedCreators?.forEach((influencer) => {
      if (!allAssignedInfluencer.includes(influencer)) {
        generalState?.showErrorToast(
          "Some influencers don’t have assigned drafts. Please assign drafts to all influencers before proceeding."
        );

        success = false;
      }
    });
    return success;
  };

  useEffect(() => {
    const length = formData?.postInfo?.postDrafts?.filter(
      (item) => item?.status
    )?.length;
    const section = document.querySelector(`#postDraftForm_${length - 1}`);
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.postInfo?.postDrafts?.length]);
  useEffect(() => {
    if (currentSection === "post") {
      const section = document.querySelector(`#Draft_top`);
      if (section) {
        section.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [currentSection]);
  useEffect(() => {
    if (formData?.postInfo?.postDrafts?.length > 0) {
      setAllAssignedInfluencer(
        formData?.postInfo?.postDrafts
          ?.filter((item) => item?.status)
          ?.flatMap((item) => item.assignedInfluencers)
      );
    } else {
      setAllAssignedInfluencer([]);
    }
  }, [formData?.postInfo?.postDrafts]);

  return (
    <>
      {currentSection === "product" ? (
        <div className="space-y-4">
          <div className="mb-8 p-4 bg-red-50 border border-red-100 rounded-lg">
            <h2 className="flex items-center gap-2 text-sm font-medium mb-2">
              <span className="i-lucide-file-text" />
              Important Guidelines
            </h2>
            <p className="text-sm text-gray-600">
              The details below inform influencers about your product and
              campaign requirements. The clearer and more to-the-point your
              information, the better the content they can create.
            </p>
          </div>

          {/* Product Details Card */}
          <Card>
            <CardHeader className="p-6">
              <div className="flex flex-col items-start gap-1">
                <CardTitle className="text-sm font-semibold text-blue-500">
                  Product Details
                </CardTitle>
                <p className="text-sm text-gray-500">
                  Provide details to help influencers accurately represent your
                  offerings
                </p>
              </div>
            </CardHeader>
            <CardContent className="p-6 pt-0 space-y-6">
              <div className="space-y-1">
                <Label htmlFor="product-name">Product/Service Name</Label>
                <p className="text-xs text-gray-500">
                  If you intend to promote multiple products together, mention
                  all of them separated by comma
                </p>
                <Input
                  className={FieldAndPlaceHolderStyles}
                  name="productName"
                  value={formData?.productInfo?.productName}
                  placeholder="e.g. Colgate Strong Teeth Toothpaste, Colgate Plax Active Salt mouthwash"
                  onChange={(e) =>
                    handleInputChange(e, "productInfo", "productName")
                  }
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="about-product">About the Product/Service</Label>
                <p className="text-xs text-gray-500">
                  Describe your product(s) in brief to give the influencers a
                  starting point
                </p>
                <QuillEditor
                  value={formData?.productInfo?.productDescription}
                  onChange={(content: string) =>
                    setFormData((prev) => {
                      return {
                        ...prev,
                        productInfo: {
                          ...prev.productInfo,
                          productDescription: content,
                        },
                      };
                    })
                  }
                  // placeholder="e.g. Colgate Strong Teeth Toothpaste with its new refreshing formula is enriched with unique Arginine technology that gives a Calcium boost thus nourishing the teeth and making them 2X stronger.Colgate Plax Active Salt mouthwash removes over 99% of germs that cause bad breath and cavities, giving you a fresher and cleaner mouth than brushing alone."
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="reference-link">
                  Product/Service Reference
                </Label>
                <p className="text-xs text-gray-500">
                  Input the link(s) to the product/service, separated by comma
                </p>
                <Input
                  className={FieldAndPlaceHolderStyles}
                  name="productLink"
                  value={formData?.productInfo?.productLink}
                  placeholder="Enter the product Link"
                  onChange={(e) =>
                    handleInputChange(e, "productInfo", "productLink")
                  }
                />
              </div>
            </CardContent>
          </Card>

          {/*  Content Creation Section */}
          <Card>
            <CardHeader className="p-6">
              <div className="flex flex-col items-start gap-1">
                <CardTitle className="text-sm font-semibold text-blue-500">
                  Content Creation
                </CardTitle>
                <p className="text-sm text-gray-500">
                  You can provide the content or the influencer(s) can create
                  it, based on what works best for your brand
                </p>
              </div>
            </CardHeader>
            <CardContent className="p-6 pt-0 space-y-6">
              <div className="space-y-6">
                <Label>Who will create the content for this campaign?</Label>
                <p className="text-sm text-gray-500 mb-2">
                  Choose whether your brand will provide ready-to-use content or
                  if the influencer(s) will create it based on the campaign
                  brief
                </p>
                <RadioGroup
                  onValueChange={(value: "Brand" | "Influencer") =>
                    setFormData((prev) => ({
                      ...prev,
                      postInfo: {
                        ...prev.postInfo,
                        CuratedBy: value,
                      },
                    }))
                  }
                  defaultValue={formData?.postInfo?.CuratedBy}
                  className="grid grid-cols-2 gap-4"
                >
                  <div className="inline-flex items-start space-x-2 border rounded-lg p-4">
                    <RadioGroupItem value="Brand" id="Brand" />
                    <Label htmlFor="Brand">
                      Brand
                      <span className="block text-xs text-gray-500">
                        You’ll supply the final content to be used in the
                        campaign
                      </span>
                    </Label>
                  </div>
                  <div className="inline-flex items-start space-x-2 border rounded-lg p-4">
                    <RadioGroupItem value="Influencer" id="Influencer" />
                    <Label htmlFor="Influencer">
                      Influencer
                      <span className="block text-xs text-gray-500">
                        The influencer will create content aligned with your
                        campaign brief & goals
                      </span>
                    </Label>
                  </div>
                </RadioGroup>
              </div>
            </CardContent>
          </Card>

          <div className="mt-6 flex justify-between">
            <Button
              onClick={() => {
                mixpanel.track(
                  "Brand Draft Campaign - Product Details Section: Back - clicked"
                );
                handlePrevious();
              }}
              variant="outline"
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <Button
              onClick={(e) => {
                mixpanel.track(
                  "Brand Draft Campaign - Product Details Section: Save and Continue - clicked"
                );
                e.preventDefault();
                if (checkProductDetailsValidations()) {
                  setCurrentSection("post");
                }

                // handleNext();
              }}
              className="flex items-center gap-2"
              style={{ backgroundColor: "#DC2627" }}
            >
              Add Deliverables
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ) : (
        <form className="space-y-4 w-full">
          <div id="Draft_top"></div>

          {/* post details */}
          <Card>
            <CardHeader className="p-6">
              <div className="flex flex-col items-start gap-1">
                <CardTitle className="text-sm font-semibold text-blue-500">
                  Campaign Requirement
                </CardTitle>
                <p className="text-sm text-gray-500">
                  Outline the key details, guidelines, and expectations to help
                  influencers align with your campaign goals
                </p>
              </div>
            </CardHeader>
            <CardContent className="p-6 pt-0">
              <Card className="p-4">
                {formData?.postInfo?.postDrafts
                  ?.filter((item) => item?.status)
                  ?.map((item, index) => {
                    return (
                      <div id={`postDraftForm_${index}`} className="mb-6">
                        {/* Campaign Requirements */}
                        <CollabFormAccordion
                          defaultValue={`draft_${index}`}
                          Header={
                            <h3 className="font-medium">Draft {index + 1}</h3>
                          }
                          ContentArray={[
                            <CampaignFormField
                              labelHead="Assign Influencers"
                              labelDesc="Select which shortlisted influencers will
                                receive this draft. You can assign all or choose
                                specific ones."
                              Children={
                                <FancyMultiSelect
                                  className={FieldAndPlaceHolderStyles}
                                  FRAMEWORKS={previewData?.SelectedInfluencersWithCPI?.map(
                                    (influencer) =>
                                      selectedCreators?.includes(
                                        influencer?._id
                                      )
                                        ? influencer
                                        : undefined
                                  )
                                    ?.filter(
                                      (influencer) =>
                                        influencer &&
                                        (!allAssignedInfluencer?.includes(
                                          influencer?._id
                                        ) ||
                                          item?.assignedInfluencers?.includes(
                                            influencer?._id
                                          ))
                                    )
                                    .map((influencer) => ({
                                      value: influencer?._id,
                                      label: influencer?.name,
                                    }))}
                                  placeholder={"Select Creators"}
                                  value={item?.assignedInfluencers}
                                  setValue={(value) => {
                                    handleDraftChange(
                                      "assignedInfluencers",
                                      value,
                                      index
                                    );
                                  }}
                                />
                              }
                              mandatory={true}
                            />,

                            <CampaignFormField
                              labelHead="Post Format"
                              labelDesc=" Select the format in which you want the collaborative
                       post to be shared"
                              Children={
                                <Select
                                  name="collabPostFormat"
                                  defaultValue={item?.postFormat}
                                  onValueChange={(value) =>
                                    handleDraftChange(
                                      "postFormat",
                                      value,
                                      index
                                    )
                                  }
                                >
                                  <SelectTrigger
                                    className={FieldAndPlaceHolderStyles}
                                  >
                                    <SelectValue placeholder="Select Post Format">
                                      {item?.postFormat
                                        ? (formData?.campaignInfo
                                            ?.collabType === "fixed"
                                            ? postFormatListForFixed
                                            : postFormatListV2
                                          )?.find(
                                            (FormatItem) =>
                                              FormatItem?.k === item?.postFormat
                                          )?.v
                                        : "Select Post Format"}
                                    </SelectValue>
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectGroup>
                                      {(formData?.campaignInfo?.collabType ===
                                      "fixed"
                                        ? postFormatListForFixed
                                        : postFormatListV2
                                      )?.map((item) => (
                                        <SelectItem
                                          key={item?.k}
                                          value={item?.k}
                                        >
                                          {/* {item?.replace("/", "+")} */}
                                          <div className="flex flex-col">
                                            <span className="font-semibold">
                                              {item?.v}
                                            </span>
                                            <span className="text-sm text-[#424242]">
                                              {item?.d}
                                            </span>
                                          </div>
                                        </SelectItem>
                                      ))}
                                    </SelectGroup>
                                  </SelectContent>
                                </Select>
                              }
                              mandatory={true}
                            />,
                            <CampaignFormField
                              labelHead={
                                formData?.postInfo?.CuratedBy === "Brand"
                                  ? " Campaign Content"
                                  : "Campaign Brief"
                              }
                              labelDesc={
                                formData?.postInfo?.CuratedBy === "Brand"
                                  ? "This is the content that influencers will use as-is. Ensure it is clear and ready for posting"
                                  : `Share the key story, angles, or product highlights to guide influencers in creating their post`
                              }
                              Children={
                                <QuillEditor
                                  value={item?.postDescription}
                                  onChange={(content: string) =>
                                    handleDraftChange(
                                      "postDescription",
                                      content,
                                      index
                                    )
                                  }
                                  //             placeholder="e.g. Get started with a smile
                                  // It's never easy to take that first step. All the doubts, all the fears, appear in that moment. New beginnings are never easy. But become easy with your smile. And your every smile gets its confidence from Colgate. So, smile & get started."
                                />
                                // <Textarea
                                //   className="placeholder:text-neutral-400 placeholder:text-sm placeholder:font-normal border bg-transparent rounded-md border-solid border-[#D5D4D4] h-32 "
                                //   placeholder="e.g. Get started with a smile
                                // It's never easy to take that first step. All the doubts, all the fears, appear in that moment. New beginnings are never easy. But become easy with your smile. And your every smile gets its confidence from Colgate. So, smile & get started."
                                //   value={formData?.postTextContent}
                                //   name="postTextContent"
                                //   onChange={handleInputChange}
                                // />
                              }
                              mandatory={true}
                            />,
                            item?.postFormat
                              .toLocaleLowerCase()
                              .includes("image") && (
                              <CampaignFormField
                                labelHead="Images of Product/Service"
                                labelDesc="Upload the images from which the influencers can pick for
                         the post (one or many)"
                                Children={
                                  <div className="space-y-2">
                                    <Input
                                      className={FieldAndPlaceHolderStyles}
                                      type="file"
                                      multiple
                                      accept=".png, .jpg"
                                      onChange={(e) =>
                                        handleDraftChange(
                                          "postImage",
                                          Array.from(e.target.files),
                                          index
                                        )
                                      }
                                    />
                                    <div className="flex flex-wrap mt-5">
                                      {item?.postImage.map(
                                        (file, imageIndex) => (
                                          <div
                                            key={imageIndex}
                                            className="relative m-[10px]"
                                          >
                                            <img
                                              className=" w-[100px] h-[100px] object-cover rounded-md "
                                              src={
                                                file instanceof Blob
                                                  ? URL.createObjectURL(file)
                                                  : file
                                              }
                                              alt={`preview-${imageIndex}`}
                                              style={{
                                                width: "100px",
                                                height: "100px",
                                                objectFit: "cover",
                                              }}
                                            />
                                            <div
                                              onClick={() =>
                                                setFormData((prev) => {
                                                  return {
                                                    ...prev,
                                                    postInfo: {
                                                      ...prev.postInfo,
                                                      postDrafts:
                                                        prev?.postInfo?.postDrafts
                                                          ?.filter(
                                                            (item) =>
                                                              item?.status
                                                          )
                                                          ?.map(
                                                            (
                                                              draft,
                                                              draftIndex
                                                            ) => {
                                                              if (
                                                                draftIndex ===
                                                                index
                                                              ) {
                                                                return {
                                                                  ...draft,
                                                                  postImage:
                                                                    draft?.postImage?.filter(
                                                                      (
                                                                        _,
                                                                        index
                                                                      ) =>
                                                                        index !==
                                                                        imageIndex
                                                                    ),
                                                                };
                                                              }
                                                              return draft;
                                                            }
                                                          ),
                                                    },
                                                  };
                                                })
                                              }
                                              className="w-4 h-4 flex items-center justify-center text-sm absolute top-[5px] right-[5px] bg-red-500 text-white rounded-full cursor-pointer  "
                                            >
                                              &times;
                                            </div>
                                          </div>
                                        )
                                      )}
                                    </div>
                                  </div>
                                }
                                // mandatory={true}
                              />
                            ),
                            item?.postFormat
                              .toLocaleLowerCase()
                              .includes("carousel") && (
                              <CampaignFormField
                                labelHead="PDF for Carousel"
                                labelDesc="Upload PDF files for the carousel post (one or many)"
                                Children={
                                  <div className="space-y-2">
                                    <Input
                                      className={FieldAndPlaceHolderStyles}
                                      type="file"
                                      multiple
                                      accept=".pdf"
                                      onChange={(e) =>
                                        handleDraftChange(
                                          "postPdf",
                                          Array.from(e.target.files),
                                          index
                                        )
                                      }
                                    />
                                    <div className="flex flex-wrap gap-6 mt-5">
                                      {item?.postPdf?.map(
                                        (itemPdf, pdfIndex) => (
                                          <div
                                            key={pdfIndex}
                                            className="relative group"
                                          >
                                            {/* PDF Card */}
                                            <div className="w-36 h-44 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl shadow-sm border border-gray-200 flex flex-col items-center justify-center transition-all duration-200 hover:shadow-md hover:border-gray-300">
                                              <div className="mb-4">
                                                <FileText
                                                  size={48}
                                                  className="text-red-500"
                                                />
                                              </div>
                                              <div className="text-gray-600 font-medium">
                                                PDF {pdfIndex + 1}
                                              </div>

                                              {/* Remove Button */}
                                              <button
                                                onClick={() =>
                                                  handleRemovePdf(
                                                    pdfIndex,
                                                    index
                                                  )
                                                }
                                                className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center bg-red-500 hover:bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                                              >
                                                &times;
                                              </button>
                                            </div>

                                            {/* Preview Button */}
                                            <div className="mt-2 flex justify-center">
                                              <Button
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  handlePreview(itemPdf);
                                                }}
                                                variant="outline"
                                                className="bg-white text-gray-700 hover:bg-gray-100 flex items-center gap-2 text-sm px-3 py-1 h-8"
                                              >
                                                <Eye size={16} />
                                                Preview
                                              </Button>
                                            </div>
                                          </div>
                                        )
                                      )}
                                    </div>
                                  </div>
                                }
                              />
                            ),
                            item?.postFormat
                              .toLocaleLowerCase()
                              .includes("video") && (
                              <CampaignFormField
                                labelHead="Videos of Product/Service"
                                labelDesc="Upload the Video which the Influencers can post"
                                Children={
                                  <Input
                                    className={FieldAndPlaceHolderStyles}
                                    type="text"
                                    placeholder="Video Link"
                                    value={item?.postVideo}
                                    name="postVideo"
                                    onChange={(e) =>
                                      handleDraftChange(
                                        "postVideo",
                                        e.target.value,
                                        index
                                      )
                                    }
                                  />
                                }
                                // mandatory={true}
                              />
                            ),
                            <CampaignFormField
                              labelHead="Campaign Guidelines"
                              labelDesc="Let influencers know your ideal content style, key
                       elements, and any legal requirements to ensure
                       everything's spot on"
                              Children={
                                <QuillEditor
                                  value={item?.postGuidelines}
                                  onChange={(content: string) =>
                                    handleDraftChange(
                                      "postGuidelines",
                                      content,
                                      index
                                    )
                                  }
                                  placeholder="Enter any guidelines for the Influencers"
                                />

                                // <Textarea
                                //   className="placeholder:text-neutral-400 placeholder:text-sm placeholder:font-normal border bg-transparent rounded-md border-solid border-[#D5D4D4] h-32 "
                                //   placeholder="Enter any guidelines for the Influencers"
                                //   value={formData?.creatorGuidelines}
                                //   name="creatorGuidelines"
                                //   onChange={handleInputChange}
                                // />
                              }
                              mandatory={true}
                            />,
                            <div className="w-full flex gap-1 items-start justify-end">
                              {
                                <>
                                  {/* //delete */}
                                  {formData?.postInfo?.postDrafts?.filter(
                                    (item) => item?.status
                                  )?.length !== 1 && (
                                    <AlertDialog>
                                      <AlertDialogTrigger asChild>
                                        <Button
                                          variant="outline"
                                          className="gap-2"
                                        >
                                          <Trash2 className="h-4 w-4" />
                                          Delete Draft
                                        </Button>
                                      </AlertDialogTrigger>
                                      <AlertDialogContent>
                                        <AlertDialogHeader>
                                          <AlertDialogTitle>
                                            Are You Sure?
                                          </AlertDialogTitle>
                                          <AlertDialogDescription>
                                            Deleting this draft is permanent and
                                            cannot be undone. Please confirm if
                                            you want to proceed with deleting
                                            this draft.
                                          </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                          <AlertDialogCancel>
                                            Cancel
                                          </AlertDialogCancel>
                                          <AlertDialogAction
                                            onClick={(e) => {
                                              e.preventDefault();
                                              handleDeleteDraft(index);
                                            }}
                                          >
                                            Delete Draft
                                          </AlertDialogAction>
                                        </AlertDialogFooter>
                                      </AlertDialogContent>
                                    </AlertDialog>
                                  )}
                                  {/* // duplicate */}
                                  <Button
                                    disabled={checkAllSelectedInfluencerValidation()}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleDuplicateDraft(item);
                                    }}
                                    variant="outline"
                                    className="gap-2 bg-[#EDEFFF]"
                                  >
                                    <Copy className="h-4 w-4" />
                                    Duplicate Draft
                                  </Button>
                                  {/* //new draft */}
                                  <Button
                                    disabled={checkAllSelectedInfluencerValidation()}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleAddNewDraft();
                                    }}
                                    className="gap-2"
                                  >
                                    <Plus className="h-4 w-4" />
                                    Create New Draft
                                  </Button>
                                </>
                              }
                            </div>,
                          ]}
                        />
                      </div>
                    );
                  })}
              </Card>

              <div className="mt-6 flex justify-between">
                <Button
                  onClick={() => {
                    mixpanel.track(
                      "Brand Draft Campaign - Product Details Section: Back - clicked"
                    );
                    setCurrentSection("product");
                  }}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                <NoActivePlanAlertButton
                  Button={
                    <Button
                      className="flex items-center gap-2"
                      style={{ backgroundColor: "#DC2627" }}
                    >
                      Save & Continue
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  }
                  onClick={(e) => {
                    mixpanel.track(
                      "Brand Draft Campaign - Product Details Section: Save and Continue - clicked"
                    );
                    e.preventDefault();

                    if (checkAllAssignedInfluencerValidation()) {
                      handleNext();
                    }
                  }}
                />
              </div>
            </CardContent>
          </Card>
        </form>
      )}
    </>
  );
};
